import React, { useCallback, useEffect, useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { Clock, Calendar, Plus, ToggleLeft, ToggleRight } from 'lucide-react';
import CustomButton from "../../../atoms/CustomButton";
import InputLayout from "../../../atoms/InputLayout";
import Error from "../../../atoms/Error";
import { deleteData, getData, patchData, postData } from "../../../../services";
import { useCookies } from "react-cookie";
import toast from "react-hot-toast";
import FormObserver from "../../../../helpers/FormObserver";
import DateRangePickerShortCuts from "../../../organisms/dateRangePicker";
import {
    addHours,
    startOfDay,
    subDays
} from "rsuite/esm/internals/utils/date";
import { formatTime } from "../../../../helpers/timeformathoursminsec";
import { DateSelector } from "../../../organisms/MonthYearSelector";
import CustomDatagrid from "../../../organisms/CustomDatagrid";
import dayjs from "dayjs";
import CustomTimePicker from "../../../atoms/MuiTimePicker";
import Modal from "../../../organisms/modals/Modal";
import { useSelector } from "react-redux";
import SelectDropdown from "../../../molecules/Dropdown";
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import DeleteModal from "../../../organisms/modals/DeleteModal";
import { FilePlus, List } from 'lucide-react';
import { getAllProjects, getClientList } from "../../../../services/basicGets";
import VerticalMenu from "../../../icons/VerticalMenu";
import CustomMenu from "../../../atoms/CustomMenu";
import Edit from "../../../icons/Edit";
import Delete from "../../../icons/Delete";

const CustomNoRowsOverlay = () => (
    <div style={{ textAlign: 'center', padding: '20px' }}>
        No tasks available
    </div>
);

const convertMinutesToHHMMSS = (totalMinutes) => {
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:00`;
};

const TeamDSRForm = ({ teamdsr, initialData, refetchData, onClose }) => {
    const [cookies] = useCookies(["e_t"]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [currentDate, setCurrentDate] = useState(
        initialData?.dsr_date
            ? new Date(initialData.dsr_date).toISOString().split("T")[0] + "T00:00:00Z"
            : new Date().toISOString().split("T")[0] + "T00:00:00Z"
    );
    const [showAddTaskForm, setShowAddTaskForm] = useState(false);
    const [showNewTaskModal, setShowNewTaskModal] = useState(false);
    const [showTimePickerModal, setShowTimePickerModal] = useState(false);
    const [selectedTask, setSelectedTask] = useState(null);
    const [totalLoggedTime, setTotalLoggedTime] = useState();
    const [taskFormType, setTaskFormType] = useState("new");
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedRow, setSelectedRow] = useState(null);
    const defaultToday = new Date();
    const [date, setDate] = useState({
        start_date: addHours(startOfDay(subDays(defaultToday, 14)), 12),
        end_date: addHours(startOfDay(defaultToday), 12),
    });
    const [taskList, setTaskList] = useState([]);
    const [projectList, setProjectList] = useState([]);
    const [clientList, setClientList] = useState([]);
    const [modalState, setModalState] = useState({
        isModalOpen: false,
        modalData: null,
        isDeleteModalOpen: false,
        modalId: "",
        name: "",
        title: ""
      });

    const isoDate = defaultToday.toISOString().split("T")[0];

    const accessData = useSelector((state) => state.accessDetails?.accessData);
    const tasksAccess = accessData?.access?.find((module) => module.module === "Tasks") || {};
    const isAdmin = accessData?.role?.label === "Admin";

    const menu = [
        {
          icon: <Delete />,
          label: "Delete",
          onClick: () => {
            setAnchorEl(null);
            handleDeleteModal(selectedRow);
          },
        },
      ];

      const handleDeleteModal = (data) => {
        setModalState((prevState) => ({
          ...prevState,
          isDeleteModalOpen: true,
          modalId: data.id,
          name: data.name,
        }));
      };

    const getUserData = useCallback(
        async (selectedDate) => {
            try {
                const encodedDate = encodeURIComponent(selectedDate);
                const res = await getData({
                    endpoint: `StatusReport/GetStatusReport?date=${encodedDate}`,
                    token: cookies.e_t,
                });
                if (res) {
                    setTaskList(res?.data?.tasks || []);
                    setTotalLoggedTime(res?.data?.totalLoggedTime);
                }
            } catch (error) {
                console.error(error);
            }
        },
        [cookies.e_t]
    );

    const today = dayjs().startOf("day");
    const yesterday = today.subtract(6, "day");

    // useEffect(() => {
    //     (async () => {
    //         const projects = await getAllProjects({
    //             ...(!isAdmin && { params: { employee_id: cookies.euid } }),
    //             token: cookies.e_t,
    //         });

    //         const clients = await getClientList({
    //             token: cookies.e_t,
    //         });

    //         const response = await getData({
    //             endpoint: "Task/GetTaskBasedOnProjectAndClient",
    //             token: cookies.e_t,
    //         });

    //         setClientList(clients?.data);
    //         setProjectList(projects?.data);
    //     })();
    // }, [projectList.length, clientList.length, cookies.e_t, isAdmin]);

    // useEffect(() => {
    //     const dateToFetch = initialData?.dsr_date
    //         ? new Date(initialData.dsr_date).toISOString().split("T")[0] + "T00:00:00Z"
    //         : new Date().toISOString().split("T")[0] + "T00:00:00Z";
    //     setCurrentDate(dateToFetch);
    //     getUserData(dateToFetch);
    // }, [initialData, getUserData]);

    useEffect(()=>{
        setTaskList(initialData?.taskSummaries)
        setTotalLoggedTime( formatTime(initialData?.totalLoggedTime));
    },[])
    const initialValues = {
        due_date: initialData?.dsr_date
            ? new Date(initialData.dsr_date).toISOString().split("T")[0] + "T00:00:00Z"
            : new Date().toISOString().split("T")[0] + "T00:00:00Z",
    };

    const validationSchema = Yup.object().shape({
        due_date: Yup.string().required("Date is required"),
    });

    const updateTaskTime = async (taskId, hours, minutes) => {
        try {
            const totalMinutes = hours * 60 + minutes;
            const timeFormatted = convertMinutesToHHMMSS(totalMinutes);

            const res = await postData({
                endpoint: "Task/updateTaskTime",
                data: {
                    task_id: taskId,
                    elapsed_time: timeFormatted,
                },
                token: cookies.e_t,
            });

            if (res) {
                const updatedTaskList = taskList.map((task) =>
                    task.id === taskId ? { ...task, hours, minutes } : task
                );
                setTaskList(updatedTaskList);

                toast.success("Task time updated successfully", {
                    style: {
                        background: "#34A853",
                        color: "#fff",
                    },
                    duration: 1000,
                });
            }
        } catch (err) {
            toast.error("Failed to update task time", {
                style: {
                    background: "#D32F2F",
                    color: "#fff",
                },
                duration: 1000,
            });
            console.error(err);
        }
    };


    const handleClose = () => {
        setAnchorEl(null);
      };

    function convertMinutesToHHMMSS(minutes) {
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        const seconds = 0;
        return `${hours.toString().padStart(2, "0")}:${mins
            .toString()
            .padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
    }

    function convertToTimeSpan(timeStr) {
        let hours = 0;
        let minutes = 0;

        const hoursMatch = timeStr.match(/(\d+)h/);
        const minutesMatch = timeStr.match(/(\d+)m/);

        if (hoursMatch) {
            hours = parseInt(hoursMatch[1], 10);
        }
        if (minutesMatch) {
            minutes = parseInt(minutesMatch[1], 10);
        }

        const totalSeconds = hours * 3600 + minutes * 60;

        const hh = Math.floor(totalSeconds / 3600)
            .toString()
            .padStart(2, "0");
        const mm = Math.floor((totalSeconds % 3600) / 60)
            .toString()
            .padStart(2, "0");
        const ss = (totalSeconds % 60).toString().padStart(2, "0");

        return `${hh}:${mm}:${ss}`;
    }

    // const handleSubmit = async (values, { resetForm }) => {
    //     setIsSubmitting(true);
    //     try {
    //         const endpoint = initialData ? "StatusReport/UpdateDSRAsync" : "StatusReport/AddDSRAsync";
    //         const method = initialData ? patchData : postData;

    //         const formattedTime = convertToTimeSpan(totalLoggedTime);
    //         const todayDate = new Date().toISOString().split("T")[0] + "T00:00:00Z";

    //         const isToday = values.due_date.split("T")[0] === todayDate.split("T")[0];

    //         const data = {
    //             total_tasks: taskList?.length || 0,
    //             total_logged_time: formattedTime,
    //             dsr_date: values.due_date,
    //             status: parseTimeToMinutes(totalLoggedTime) > 1440 ? "Rejected" :  isToday ? "Approved" : "Pending",
    //             type: "daily",
    //         };

    //         const res = await method({
    //             endpoint,
    //             data,
    //             params: initialData?.id ? { dsrId: initialData.id } : {},
    //             token: cookies.e_t,
    //         });

    //         if (res) {
    //             toast.success(`DSR Entry ${initialData ? "Updated" : "Created"} Successfully`, {
    //                 style: { background: "#34A853", color: "#fff" },
    //                 duration: 1000,
    //             });
    //             resetForm();
    //         }
    //     } catch (err) {
    //         console.error("Error submitting DSR:", err);
    //         toast.error("An Error Occurred: " + (err.message || "Unknown error"), {
    //             style: { background: "#D32F2F", color: "#fff" },
    //             duration: 1000,
    //         });
    //     } finally {
    //         setIsSubmitting(false);
    //         onClose();
    //         refetchData();
    //     }
    // };

    // const handleNewTaskSubmit = async (values, { resetForm }) => {
    //     try {
    //         const taskData = {
    //             project_id: values.project,
    //             client_id: values.client,
    //             priority: values.priority,
    //             status: values.status,
    //             name: values.taskTitle,
    //             media: [],
    //             assigned_employees: [cookies.euid],
    //         };
    //         const params = {
    //             elapsed_time: values.elapsed_time,
    //             taskTimer_date: currentDate,
    //         };

    //         const res = await postData({
    //             endpoint: "StatusReport/AddTaskTimerWhileCreatingTask",
    //             params: params,
    //             data: taskData,
    //             token: cookies.e_t,
    //         });

    //         if (res) {
                
    //             resetForm();
    //             setShowNewTaskModal(false);
    //             getUserData(currentDate);
    //         }
    //     } catch (err) {
    //         toast.error("Failed to Add Task", {
    //             style: {
    //                 background: "#D32F2F",
    //                 color: "#fff",
    //             },
    //             duration: 1000,
    //         });
    //         console.error(err);
    //     }
    // };

    // const handleExistingTaskSubmit = async (values, { resetForm }) => {
    //     try {
    //         const taskData = {
    //             task_id: values.taskList,
    //             elapsed_time: values.elapsed_time,
    //             assignee: cookies.euid,
    //             date: currentDate,
    //         };

    //         const res = await postData({
    //             endpoint: "TaskTimer/addTimerLog",
    //             data: taskData,
    //             token: cookies.e_t,
    //         });

    //         if (res) {
              
    //             resetForm();
    //             setShowNewTaskModal(false);
    //             getUserData(currentDate);
    //         }
    //     } catch (err) {
    //         toast.error("Failed to Update Task Time", {
    //             style: {
    //                 background: "#D32F2F",
    //                 color: "#fff",
    //             },
    //             duration: 1000,
    //         });
    //         console.error(err);
    //     }
    // };

    // const handleDeletion = async () => {
    //     try {
    //       const res = await deleteData({
    //         endpoint: `StatusReport/DeleteDSRAsync?id=${modalState.modalId}`,
    //         token: cookies.e_t,
    //       });
          
    //       if (res) {
    //         // getDSRData();
    //         toast.success("DSR Entry Deleted Successfully", {
    //           style: { 
    //             background: '#34A853', 
    //             color: '#fff' 
    //           },
    //           duration: 1000,
    //         });
    //       }
    //     } catch (error) {
    //       toast.error("An Error Occurred", {
    //         style: { 
    //           background: '#D32F2F', 
    //           color: '#fff' 
    //         },
    //         duration: 1000,
    //       });
    //       console.error(error);
    //     } finally {
    //       setModalState((prevState) => ({
    //         ...prevState,
    //         isDeleteModalOpen: false,
    //       }));
    //     }
    //   };

    const handleTimePickerSelect = (minutes) => {
        if (selectedTask) {
            const hours = Math.floor(minutes / 60);
            const mins = minutes % 60;

            updateTaskTime(selectedTask.id, hours, mins);
            setShowTimePickerModal(false);
        }
    };

    const handleStatusMenu = (event, row) => {
        setAnchorEl(event.currentTarget);
        setSelectedRow(row);
      };

    const toggleTaskFormType = (event, newValue) => {
        if (newValue !== null) {
            setTaskFormType(newValue);
        }
    };

    const TimeInput = ({ field, form, ...props }) => {
        return (
            <input
                type="number"
                {...field}
                {...props}
                className="form-control"
                style={{ width: "80px" }}
            />
        );
    };

    function parseTimeToMinutes(timeStr) {
        if (!timeStr || typeof timeStr !== "string") return 0;
    
        const hoursMatch = timeStr.match(/(\d+)h/);
        const minutesMatch = timeStr.match(/(\d+)m/);
    
        const hours = hoursMatch ? parseInt(hoursMatch[1], 10) : 0;
        const minutes = minutesMatch ? parseInt(minutesMatch[1], 10) : 0;
    
        return hours * 60 + minutes;
    }
    
    

    const columns = [
        { 
            field: "taskName", 
            headerName: "Task Name", 
            flex: 7, 
            disableColumnMenu: true,
            renderCell: (params) => (
              <div style={{
                display: '-webkit-box',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: 2,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'normal',
                lineHeight: '1.2rem',
                maxHeight: '2.4rem'
              }}>
                {params.value}
              </div>
            )
          },
        {
            field: "totalElapsedTime",
            headerName: "Log Time",
            flex: 3,
            disableColumnMenu: true,
            // renderCell: (params) => {
            //     console.log("This is param",params)
            //     const minutes = params.value;
            //     if (!minutes && minutes !== 0) return <span>0 min</span>;
            //     const hours = Math.floor(minutes / 60);
            //     const remainingMinutes = minutes % 60;

            //     if (hours === 0) {
            //         return <span>{remainingMinutes} min</span>;
            //     } else {
            //         return (
            //             <span>
            //                 {hours}:{remainingMinutes.toString().padStart(2, "0")} hrs
            //             </span>
            //         );
            //     }
            // },
        },
        // {
        //     field: "actions",
        //     headerName: "",
        //     flex: 0.2,
        //     renderCell: (params) => {
        //       return (
        //         <div className="table-icons-container">
        //           <div onClick={(event) => handleStatusMenu(event, params.row)}>
        //             <VerticalMenu />
        //           </div>
      
        //           <CustomMenu
        //             anchorEl={anchorEl}
        //             open={Boolean(anchorEl)}
        //             onClose={handleClose}
        //             items={menu}
        //           />
        //         </div>
        //       );
        //     },
        //   },
    ];

    return (
        <>
            <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                validateOnBlur={false}
                validateOnChange={false}
                enableReinitialize
            >
                {({ values, touched, errors, handleBlur, setFieldValue, setFieldTouched }) => (
                    <Form>
                        <div className="dsr-form-container">
                            <div className="" style={{ display: "flex" }}>
                                <div
                                    className="task-list-panel"
                                    style={{
                                        flex: "0 0 60%",
                                        borderRight: "1px solid #dee2e6",
                                        paddingRight: "20px",
                                    }}
                                >
                                    <div
                                        className="task-list"
                                        style={{
                                            height: "250px",
                                            overflowY: "auto",
                                            border: "1px solid #dee2e6",
                                            borderRadius: "4px",
                                        }}
                                    >
                                        <CustomDatagrid
                                            rows={taskList}
                                            columns={columns}
                                            getRowId={(row) => row.taskId}
                                            isLoading={false}
                                            isLoadingStateProvided={true}
                                            slots={{
                                                noRowsOverlay: CustomNoRowsOverlay,
                                            }}
                                            sx={{
                                                border: "none",
                                                "& .MuiDataGrid-cell:focus": { outline: "none" },
                                                height: "250px",
                                            }}
                                            rowHeight={40}
                                            headerHeight={42}
                                        />
                                    </div>

                                    {/* {!teamdsr && (<div className="add-task mt-4">
                                        <button
                                            type="button"
                                            className="btn btn-outline-primary"
                                            onClick={() => {
                                                setShowNewTaskModal(true);
                                                setTaskFormType("new");
                                            }}
                                        >
                                            <Plus size={18} className="me-2" />
                                            Add Task
                                        </button>
                                    </div>
                                    )} */}
                                </div>

                                <div
                                    className="summary-panel"
                                    style={{
                                        display: "flex",
                                        flex: "0 0 40%",
                                        paddingLeft: "20px",
                                        flexDirection: "column",
                                    }}
                                >
                                    <div className="date-selector-container">
                                        <DateSelector
                                            placeholder=""
                                            selectedDate={values.due_date}
                                            onChange={(date) => {
                                                const utcDateISO =
                                                    new Date(date).toISOString().split("T")[0] +
                                                    "T00:00:00Z";
                                                setFieldValue("due_date", utcDateISO);
                                                setCurrentDate(utcDateISO);
                                                getUserData(utcDateISO);
                                            }}
                                            handleBlur={(e) => {
                                                setFieldTouched("due_date", true);
                                                handleBlur(e);
                                            }}
                                            disablePast={false}
                                            disableFuture={false}
                                            minDateData={yesterday}
                                            maxDateData={today}
                                            disabled={!!initialData?.created_at}
                                            isError={touched.due_date && errors.due_date}
                                        />
                                    </div>

                                    <div
                                        className="summary-content"
                                        style={{
                                            flex: "1",
                                            display: "flex",
                                            flexDirection: "column",
                                            paddingTop: "30px",
                                            justifyContent: "space-between",
                                        }}
                                    >
                                        <div>
                                            <h6 style={{ marginBottom: "12px", fontWeight: "bolder" }}>
                                                Summary
                                            </h6>
                                            <hr style={{ marginBottom: "16px" }} />

                                            <div
                                                style={{
                                                    display: "flex",
                                                    justifyContent: "space-between",
                                                    marginBottom: "8px",
                                                }}
                                            >
                                                <span>Total Tasks:</span>
                                                <span style={{ fontWeight: "bold" }}>
                                                    {taskList.length}
                                                </span>
                                            </div>

                                            <div
                                                style={{
                                                    display: "flex",
                                                    justifyContent: "space-between",
                                                    marginBottom: "8px",
                                                }}
                                            >
                                                <span>Total Hours:</span>
                                                <span style={{ fontWeight: "bold" }}>
                                                    {totalLoggedTime}
                                                </span>
                                            </div>

                                            {parseTimeToMinutes(totalLoggedTime) > 1440 && (
                                                <div
                                                    style={{
                                                        backgroundColor: "#ffebee",
                                                        padding: "7px 15px",
                                                        borderRadius: "12px",
                                                        marginTop: "10px",
                                                        color: "#d32f2f",
                                                        fontWeight: "400",
                                                        textAlign: "center"
                                                    }}
                                                >
                                                    Warning: Time exceeds 24 hours
                                                </div>
                                            )}
                                        </div>

                                        {!teamdsr && (
                                            <div
                                                className="mt-4"
                                                style={{
                                                    display: "flex",
                                                    justifyContent: "flex-end",
                                                    gap: "10px",
                                                    marginTop: "auto",
                                                }}
                                            >
                                                <button
                                                    type="button"
                                                    className="btn btn-outline-primary"
                                                    onClick={onClose}
                                                >
                                                    Cancel
                                                </button>
                                                <button
                                                    type="submit"
                                                    className="btn btn-primary"
                                                    disabled={isSubmitting}
                                                >
                                                    {initialData ? "Update" : "Save"}
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Form>
                )}
            </Formik>

            {/* {showNewTaskModal && (
                <Modal
                    id="new-task-modal"
                    title={taskFormType === "new" ? "Add Logs" : "Add Logs"}
                    isActive={showNewTaskModal}
                    onClose={() => {
                        setShowNewTaskModal(false);
                        setShowAddTaskForm(false);
                    }}
                    takeConfirmation={false}
                    width="600px"
                    extrastyle={{ overflow: "visible" }}
                >
                    <div className="d-flex mb-4">
                        <ToggleButtonGroup
                            value={taskFormType}
                            exclusive
                            onChange={toggleTaskFormType}
                            aria-label="task type"
                            sx={{
                                "& .MuiToggleButton-root": {
                                    padding: "8px 16px",
                                    border: "1px solid rgba(0, 0, 0, 0.12)",
                                    textTransform: "none",
                                    "&.Mui-selected": {
                                        backgroundColor: "#6C5DD3",
                                        color: "white",
                                        "&:hover": {
                                            backgroundColor: "#6C5DD3",
                                        },
                                    },
                                    "&:hover": {
                                        backgroundColor: "rgba(25, 118, 210, 0.08)",
                                    },
                                },
                            }}
                        >
                            <ToggleButton value="new" aria-label="new task">
                                <FilePlus size={16} className="me-2" />
                                New Task
                            </ToggleButton>
                            <ToggleButton value="existing" aria-label="existing task">
                                <List size={16} className="me-2" />
                                Existing Task
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </div>
                    {taskFormType === "new" ? (
                        <NewTaskForm
                            onClose={() => {
                                setShowNewTaskModal(false);
                                setShowAddTaskForm(false);
                            }}
                            onSubmit={handleNewTaskSubmit}
                            projectList={projectList}
                            clientList={clientList}
                        />
                    ) : (
                        <ExistingTaskForm
                            onClose={() => {
                                setShowNewTaskModal(false);
                                setShowAddTaskForm(false);
                            }}
                            onSubmit={handleExistingTaskSubmit}
                            projectList={projectList}
                            clientList={clientList}
                            initialTaskList={taskList}
                        />
                    )}
                </Modal>
            )} */}

            {modalState.isDeleteModalOpen && (
                <DeleteModal
                    name={modalState.name}
                    id={modalState.modalId}
                    isActive={modalState.isDeleteModalOpen}
                    onClose={() =>
                        setModalState((prevState) => ({
                            ...prevState,
                            isDeleteModalOpen: false,
                        }))
                    }
                    title="DSR Entry"
                    // onClick={handleDeletion}
                />
            )}

            {/* {showTimePickerModal && selectedTask && (
                <Modal
                    id="time-picker-modal"
                    title={`Update Time for ${selectedTask.name}`}
                    isActive={showTimePickerModal}
                    onClose={() => setShowTimePickerModal(false)}
                    takeConfirmation={false}
                    width="400px"
                    extrastyle={{ overflow: "visible" }}
                >
                    <div className="p-3">
                        <div className="mb-4">
                            <p>Select time spent on this task:</p>
                            <CustomTimePicker
                                onChange={handleTimePickerSelect}
                                selectedTime={`${String(selectedTask.hours || 0).padStart(
                                    2,
                                    "0"
                                )}:${String(selectedTask.minutes || 0).padStart(2, "0")}:00`}
                            />
                        </div>
                        <div className="d-flex justify-content-end">
                            <button
                                type="button"
                                className="btn btn-outline-primary me-2"
                                onClick={() => setShowTimePickerModal(false)}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </Modal>
            )} */}
        </>
    );
};

export default TeamDSRForm;